import { useEffect, useCallback, useRef } from 'react';

/**
 * useDebounce — universal debounce hook for any callback.
 *
 * @param {function} callback - Function to debounce
 * @param {number} delay - Debounce delay in ms
 * @returns {function} Debounced callback
 *
 * @example
 * const debouncedSave = useDebounce((value) => saveToServer(value), 500);
 * debouncedSave(inputValue);
 */
const useDebounce = (callback, delay) => {
  const timerRef = useRef(null);

  const debouncedCallback = useCallback(
    (...args) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return debouncedCallback;
};

export default useDebounce;
